<template>
  <Dropdown
    v-model="selectedEpisode"
    :options="episodesList"
    option-label="title"
    option-value="episode_id"
    placeholder="All Episodes"
    class="tw-border-none tw-outline-none tw-shadow-none tw-bg-none tw-bg-transparent tw-items-center tw-max-w-full sm:tw-max-w-96"
    input-class="tw-text-3xl md:tw-text-[40px] tw-font-black tw-p-0 tw-leading-normal tw-font-heading"
    panel-class="tw-max-w-xs md:tw-max-w-sm lg:tw-max-w-lg xl:tw-max-w-2xl"
    :pt="{
      panel: { style: 'width: 100%; min-width: 300px; max-width:720px;' },
      list: { style: 'overflow-x: hidden;' },
    }"
    filter
    :filterFields="['title']"
    :virtualScrollerOptions="virtualScrollerOptions"
    :loading="loading"
    @change="emits('selectEpisode', selectedEpisode)"
    @filter="onFilter"
  >
  </Dropdown>
</template>

<script setup>
import Dropdown from "primevue/dropdown";
import { computed, onMounted, ref, watch, defineEmits, defineProps } from "vue";
import { useStore } from "vuex";
import { usePaginatedFetcher } from "@/composables/usePaginatedFetcher";

const emits = defineEmits(["selectEpisode"]);
const props = defineProps(["selectedShow"]);

const store = useStore();
const token = localStorage.getItem("podcasterToken");

const selectedEpisode = ref("all");
const maxLoadedPage = ref(-1);
const pendingPage = ref(null);

const buildParams = () =>
  props.selectedShow && props.selectedShow !== "all"
    ? { showIds: props.selectedShow }
    : {};

const {
  items: episodes,
  total: totalEpisodes,
  rows,
  searchTerm,
  loading,
  fetchPage: fetchEpisodes,
  reset,
} = usePaginatedFetcher(
  (params) => store.dispatch("loadEpisodes", { token, params }),
  {
    getExtraParams: buildParams,
    initialRows: 50,
    accumulate: true,
    debounceMs: 400,
  }
);

const episodesList = computed(() => {
  const allEpisodesOption = { episode_id: "all", title: "All Episodes" };
  if (props.selectedShow === "all") return [allEpisodesOption];
  if (!episodes.value.length) return [allEpisodesOption];
  return [allEpisodesOption, ...episodes.value];
});

const loadPage = async (pageToLoad = 0) => {
  if (loading.value) return;
  if (pendingPage.value !== null) return;
  if (pageToLoad <= maxLoadedPage.value) return;

  pendingPage.value = pageToLoad;
  try {
    await fetchEpisodes(pageToLoad);
    maxLoadedPage.value = Math.max(maxLoadedPage.value, pageToLoad);
  } finally {
    pendingPage.value = null;
  }
};

const onLazyLoad = (event) => {
  if (loading.value || pendingPage.value !== null) return;

  rows.value = event.rows || rows.value;
  const first = event.first || 0;
  const visible = event.rows || rows.value;
  const remaining = episodes.value.length - (first + visible);

  if (episodes.value.length >= totalEpisodes.value) return;
  // load in advance if close to the list end
  if (remaining <= 2) {
    const nextPage = maxLoadedPage.value + 1;
    loadPage(nextPage);
  }
};

const onFilter = (event) => {
  searchTerm.value = event.value || "";
  reset({ keepSearch: true });
  maxLoadedPage.value = -1;
  loadPage(0);
};

const virtualScrollerOptions = computed(() => ({
  lazy: true,
  onLazyLoad,
  itemSize: 44,
  style: { width: "100%", overflowX: "hidden" },
  loading: loading.value,
  showLoader: true,
}));

onMounted(() => {
  loadPage(0);
});

watch(
  () => props.selectedShow,
  () => {
    selectedEpisode.value = "all";
    reset();
    maxLoadedPage.value = -1;
    loadPage(0);
  }
);

watch(selectedEpisode, (value) => {
  if (value === "all") return;
});
</script>
<style>
.p-dropdown-panel .p-dropdown-item {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}

.p-dropdown-panel .p-virtualscroller {
  width: 100% !important;
  overflow-x: hidden !important;
}

.p-dropdown-panel .p-virtualscroller-content {
  width: 100% !important;
}
</style>
